import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { AnimatedText } from '../components/AnimatedText';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

const IntegrationNode: React.FC<{
  label: string;
  icon: string;
  x: number;
  y: number;
  delay: number;
  color: string;
}> = ({ label, icon, x, y, delay, color }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;

  if (f < 0) return null;

  const scale = spring({ frame: f, fps, config: { damping: 12, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <div
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: 20,
          background: `linear-gradient(135deg, ${color}25, ${color}10)`,
          border: `2px solid ${color}50`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: 36,
          boxShadow: `0 0 30px ${color}20`,
        }}
      >
        {icon}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: COLORS.white,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {label}
      </div>
    </div>
  );
};

const DUR = SCENES.INTEGRATIONS.duration;

export const IntegrationsScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 15, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const pulse = Math.sin(frame * 0.08) * 0.15 + 0.85;

  const integrations = [
    { label: 'SAP S/4HANA', icon: '🏗️', x: 20, y: 30, delay: 25, color: COLORS.primary },
    { label: 'Salesforce', icon: '☁️', x: 80, y: 30, delay: 40, color: COLORS.cyan },
    { label: 'Odoo', icon: '⚙️', x: 20, y: 70, delay: 55, color: COLORS.violet },
    { label: 'SAP C4C', icon: '🤝', x: 80, y: 70, delay: 70, color: COLORS.emerald },
    { label: 'VTEX', icon: '🛒', x: 15, y: 50, delay: 85, color: COLORS.amber },
    { label: 'Shopify', icon: '🛍️', x: 85, y: 50, delay: 95, color: COLORS.emerald },
  ];

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="default" />

      <div style={{ position: 'absolute', top: 30, left: 0, right: 0 }}>
        <AnimatedText
          text="Integraciones Enterprise"
          fontSize={48}
          fontWeight={800}
          mode="scaleIn"
          startFrame={5}
        />
      </div>

      <svg
        style={{
          position: 'absolute',
          top: 100,
          left: 80,
          right: 80,
          width: 'calc(100% - 160px)',
          height: 680,
        }}
        viewBox="0 0 1760 680"
        preserveAspectRatio="none"
      >
        {integrations.map((int, i) => {
          const startX = (int.x / 100) * 1760;
          const startY = ((int.y - 10) / 100) * 700;
          const dashOffset = interpolate(frame, [int.delay, int.delay + 40], [500, 0], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          });
          return (
            <line
              key={i}
              x1={startX}
              y1={startY}
              x2={880}
              y2={350}
              stroke={int.color}
              strokeWidth={2}
              strokeDasharray="8 4"
              strokeDashoffset={dashOffset}
              opacity={0.4}
            />
          );
        })}
      </svg>

      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) scale(${pulse})`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${COLORS.primary}40, ${COLORS.violet}30)`,
            border: `2px solid ${COLORS.primary}60`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 4,
            boxShadow: `0 0 60px ${COLORS.primary}30`,
            opacity: interpolate(frame, [20, 40], [0, 1], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            }),
          }}
        >
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              fontFamily: 'Inter, system-ui, sans-serif',
              background: COLORS.gradientBlue,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            PRIXIA
          </div>
          <div
            style={{
              fontSize: 10,
              color: COLORS.gray,
              fontFamily: 'Inter, system-ui, sans-serif',
              letterSpacing: 2,
            }}
          >
            HUB
          </div>
        </div>
      </div>

      <div style={{ position: 'absolute', top: 100, left: 80, right: 80, height: 680 }}>
        {integrations.map((int) => (
          <IntegrationNode key={int.label} {...int} />
        ))}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 32,
          opacity: interpolate(frame, [110, 125], [0, 1], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          }),
        }}
      >
        {['Materiales', 'Clientes', 'Precios', 'Cotizaciones'].map((item) => (
          <div
            key={item}
            style={{
              padding: '8px 20px',
              borderRadius: 100,
              border: `1px solid ${COLORS.primary}30`,
              background: `${COLORS.primary}10`,
              fontSize: 13,
              fontWeight: 500,
              color: COLORS.primaryLight,
              fontFamily: 'Inter, system-ui, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: COLORS.emerald,
                boxShadow: `0 0 6px ${COLORS.emerald}`,
              }}
            />
            {item}
          </div>
        ))}
      </div>

      <SyncedSubtitles lines={SUBS.INTEGRATIONS} />
    </AbsoluteFill>
  );
};
